import React, { useEffect, useRef, useState } from "react";
import BackIcon from "../../images/BackIcon";
import SaveIcon from "../../images/SaveIcon";
import DatePicker from "react-datepicker";
import PlusGreenIcon from "../../images/PlusGreenIcon";
import DeleteIcon from "../../images/DeleteIcon";
import CloseIcon from "../../images/CloseIcon";
import {
  webLoadingFalse,
  webLoadingTrue,
  webNavigate,
} from "../../services/navigate.service";
import {
  createJournalEntry,
  updateJournalEntry,
  getJournalEntry,
  confirmJournalEntry,
  cancelJournalEntry,
  JournalEntry,
} from "../../services/journalEntries.service";
import TagStatus from "../../components/TagStatus";
import BalanceIcon from "../../images/BalanceIcon";
import { useResponsive } from "../../hook/useResponsive";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import { useParams } from "react-router-dom";
import { DropdownSearch } from "../../components/DropdownSearch";
import { useAsyncDropdown } from "../../hook/useAsyncDropdown";
import AxiosInstance from "../../config/AxiosInstance";
import { getTransactionMonth } from "../../services/purchases.services";

interface ChartOfAccount {
  id: string;
  accountCode: string;
  accountName: string;
}

interface JournalLineRow {
  id: string;
  chartOfAccountId: string;
  debit: number;
  credit: number;
  description: string;
}

const CreateJournalEntry = () => {
  const { id } = useParams<{ id: string }>();
  const preLanguage = localStorage.getItem("language");
  const [language, setLanguage] = useState<any>(null);
  const { isMobile, getWidth } = useResponsive();
  const [loading, setLoading] = useState<boolean>(false);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showBalanceError, setShowBalanceError] = useState<boolean>(false);
  
  const companyId = localStorage.getItem("companyId") || "";
  const locationId = localStorage.getItem("locationId") || "";
  const userId = localStorage.getItem("userId") || "";
  const calculatedWidth = window.innerWidth / 1290;

  const [formValues, setFormValues] = useState<any>({
    date: new Date(),
    locationId: locationId || null,
    transactionMonthsId: "",
    entryType: 1,
    comments: "",
  });

  const [lines, setLines] = useState<JournalLineRow[]>([]);
  const [entry, setEntry] = useState<JournalEntry | null>(null);
  const [listMonths, setListMonths] = useState<any[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState<boolean>(false);
  const monthRef = useRef<HTMLDivElement>(null);
  
  const [openDropdownIndexes, setOpenDropdownIndexes] = useState<{
    [key: string]: boolean;
  }>({});
  const dropdownRefs = useRef<{ [key: string]: React.RefObject<HTMLDivElement> }>({});
  const [coaSelectedLabelMap, setCoaSelectedLabelMap] = useState<Record<string, string>>({});
  const PAGE_SIZE_COA = 50;

  const coaFetch = React.useCallback(
    async ({
      page,
      search,
      limit,
      signal,
    }: {
      page: number;
      search: string;
      limit: number;
      signal?: AbortSignal;
    }) => {
      const response = await AxiosInstance.get("/api/system-accounts", {
        params: {
          page: page.toString(),
          limit: limit.toString(),
          ...(search.trim() ? { search: search.trim() } : {}),
        },
        signal,
      });
      const accounts = response.data?.data ?? response.data ?? [];
      const transformed: ChartOfAccount[] = (Array.isArray(accounts) ? accounts : []).map((acc: any) => ({
        id: acc.id,
        accountCode: acc.accountCode ?? "",
        accountName: acc.accountName ?? "",
      }));
      return {
        data: transformed,
        hasMore: Array.isArray(accounts) && accounts.length >= limit,
      };
    },
    []
  );
  const asyncCoa = useAsyncDropdown<ChartOfAccount>({
    fetchFn: coaFetch,
    limit: PAGE_SIZE_COA,
    getItemId: (item) => item.id,
    resetDeps: [companyId],
  });
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (id && id !== "new") {
      loadJournalEntry(id);
    } else {
      addLine();
    }
    loadTransactionMonths(id && id !== "new" ? null : "");
  }, [id]);

  const loadJournalEntry = async (entryId: string) => {
    webLoadingTrue();
    setLoading(true);
    try {
      const response = await getJournalEntry(entryId);
      const entryData = response.data;
      setEntry(entryData);
      
      setFormValues({
        date: new Date(entryData.date),
        locationId: entryData.locationId,
        transactionMonthsId: entryData.transactionMonthsId || "",
        entryType: entryData.entryType,
        comments: entryData.comments || "",
      });

      // Load transaction months and set selected month if editing
      if (entryData.transactionMonthsId) {
        loadTransactionMonths(entryData.transactionMonthsId);
      } else {
        loadTransactionMonths("");
      }

      setIsReadOnly((entryData.status || "").toLowerCase() !== "draft");

      const entryLines: JournalLineRow[] = (entryData.lines || [])
        .filter((l: { isDeleted?: boolean }) => !l.isDeleted)
        .map((line: { id?: string; chartOfAccountId?: string; debit?: number; credit?: number; description?: string }, index: number) => ({
          id: line.id || `temp-${index}`,
          chartOfAccountId: line.chartOfAccountId,
          debit: Number(line.debit || 0),
          credit: Number(line.credit || 0),
          description: line.description || "",
        }));
      setLines(entryLines);
      
      entryLines.forEach((line) => {
        if (!dropdownRefs.current[line.id]) {
          dropdownRefs.current[line.id] = React.createRef<HTMLDivElement>();
        }
      });
    } catch (error) {
      console.error("Error loading journal entry:", error);
      showApiError(error, {
        language,
        titleFallback: language && language?.["Error loading journal entry"]
          ? language?.["Error loading journal entry"]
          : "Error loading journal entry",
      });
    } finally {
      setLoading(false);
      webLoadingFalse();
    }
  };

  const loadTransactionMonths = async (transactionMonthsId: string | null = null) => {
    try {
      const response: any = await getTransactionMonth(locationId, companyId);
      // Filter out deleted transaction months and sort by date desc (latest first)
      const filteredMonths = (response.data || [])
        .filter((month: any) => !month.isDeleted)
        .sort((a: any, b: any) => {
          const dateA = new Date(a.fromDate).getTime();
          const dateB = new Date(b.fromDate).getTime();
          return dateB - dateA; // Descending order (latest first)
        });
      
      setListMonths(filteredMonths);

      if (transactionMonthsId) {
        // Editing existing entry - select the entry's transaction month
        const month = filteredMonths.find((m: any) => m.id === transactionMonthsId);
        if (month) {
          setSelectedMonth(`${month.transactionYear} - ${month.monthSequence}`);
          setFormValues((prev: any) => ({
            ...prev,
            transactionMonthsId: month.id,
          }));
        }
      } else {
        // New entry - auto-select the first (latest) transaction month
        if (filteredMonths.length > 0) {
          const latestMonth = filteredMonths[0];
          setSelectedMonth(`${latestMonth.transactionYear} - ${latestMonth.monthSequence}`);
          setFormValues((prev: any) => ({
            ...prev,
            transactionMonthsId: latestMonth.id,
          }));
        }
      }
    } catch (error) {
      console.error("Error loading transaction months:", error);
    }
  };

  const handleMonthSelect = (month: any) => {
    setSelectedMonth(`${month.transactionYear} - ${month.monthSequence}`);
    setFormValues((prev: any) => ({
      ...prev,
      transactionMonthsId: month.id,
    }));
    setIsMonthDropdownOpen(false);
  };

  const addLine = () => {
    if (isReadOnly) return;
    const newLine: JournalLineRow = {
      id: `temp-${Date.now()}-${Math.random()}`,
      chartOfAccountId: "",
      debit: 0,
      credit: 0,
      description: "",
    };
    setLines([...lines, newLine]);
    if (!dropdownRefs.current[newLine.id]) {
      dropdownRefs.current[newLine.id] = React.createRef<HTMLDivElement>();
    }
  };

  const removeLine = (lineId: string) => {
    if (isReadOnly) return;
    setLines(lines.filter((line) => line.id !== lineId));
  };

  const updateLine = (lineId: string, field: keyof JournalLineRow, value: any) => {
    if (isReadOnly) return;
    setLines(
      lines.map((line) =>
        line.id === lineId ? { ...line, [field]: value } : line
      )
    );
  };

  const calculateTotals = () => {
    const totalDebit = lines.reduce((sum, line) => sum + Number(line.debit || 0), 0);
    const totalCredit = lines.reduce((sum, line) => sum + Number(line.credit || 0), 0);
    return { totalDebit, totalCredit, difference: Math.abs(totalDebit - totalCredit) };
  };

  const validateEntry = (): string | null => {
    const { totalDebit, totalCredit, difference } = calculateTotals();

    if (!formValues.transactionMonthsId) {
      return language && language?.["Month is required"]
        ? language?.["Month is required"]
        : "Month is required";
    }

    if (lines.length === 0) {
      return language && language?.["At least one line is required"]
        ? language?.["At least one line is required"]
        : "At least one line is required";
    }

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line.chartOfAccountId) {
        return language && language?.[`Line ${i + 1} is missing chart of account`]
          ? language?.[`Line ${i + 1} is missing chart of account`]
          : `Line ${i + 1} is missing chart of account`;
      }
      if (line.debit > 0 && line.credit > 0) {
        return language && language?.[`Line ${i + 1} cannot have both debit and credit`]
          ? language?.[`Line ${i + 1} cannot have both debit and credit`]
          : `Line ${i + 1} cannot have both debit and credit`;
      }
      if (line.debit === 0 && line.credit === 0) {
        return language && language?.[`Line ${i + 1} must have either debit or credit`]
          ? language?.[`Line ${i + 1} must have either debit or credit`]
          : `Line ${i + 1} must have either debit or credit`;
      }
    }

    if (difference > 0.01) {
      return language && language?.["Debits and credits don't match"]
        ? language?.["Debits and credits don't match"]
        : "Debits and credits don't match";
    }

    return null;
  };

  const handleSave = async () => {
    const validationError = validateEntry();
    if (validationError) {
      showToast({
        message: validationError,
        type: "error",
        duration: 20000,
      });
      return;
    }

    webLoadingTrue();
    setLoading(true);
    try {
      if (id && id !== "new") {
        const updatePayload: any = {
          locationId: formValues.locationId || null,
          transactionMonthsId: formValues.transactionMonthsId,
          date: formValues.date.toISOString().split("T")[0],
          entryType: formValues.entryType || 1,
          comments: formValues.comments || null,
          lines: lines.map((line) => ({
            chartOfAccountId: line.chartOfAccountId,
            debit: Number(line.debit || 0),
            credit: Number(line.credit || 0),
            description: line.description || null,
          })),
        };
        await updateJournalEntry(id, updatePayload);
        showToast({
          message: language && language?.["Journal entry updated successfully"]
            ? language?.["Journal entry updated successfully"]
            : "Journal entry updated successfully",
          type: "success",
          duration: 20000,
        });
      } else {
        const createPayload: any = {
          companyId,
          locationId: formValues.locationId || null,
          transactionMonthsId: formValues.transactionMonthsId,
          date: formValues.date.toISOString().split("T")[0],
          entryType: formValues.entryType || 1,
          comments: formValues.comments || null,
          lines: lines.map((line) => ({
            chartOfAccountId: line.chartOfAccountId,
            debit: Number(line.debit || 0),
            credit: Number(line.credit || 0),
            description: line.description || null,
          })),
        };
        await createJournalEntry(createPayload);
        showToast({
          message: language && language?.["Journal entry created successfully"]
            ? language?.["Journal entry created successfully"]
            : "Journal entry created successfully",
          type: "success",
          duration: 20000,
        });
      }
      
      webNavigate("/" + preLanguage + "/journal-entries");
    } catch (error: any) {
      console.error("Error saving journal entry:", error);
      showApiError(error, {
        language,
        titleFallback: language && language?.["Error saving journal entry"]
          ? language?.["Error saving journal entry"]
          : "Error saving journal entry",
      });
    } finally {
      setLoading(false);
      webLoadingFalse();
    }
  };

  const handleConfirm = async () => {
    const validationError = validateEntry();
    if (validationError) {
      setShowBalanceError(true);
      setShowConfirmModal(false);
      return;
    }

    webLoadingTrue();
    setLoading(true);
    try {
      let entryIdToConfirm = id && id !== "new" ? id : null;
      if (!entryIdToConfirm) {
        const createPayload: any = {
          companyId,
          locationId: formValues.locationId || null,
          transactionMonthsId: formValues.transactionMonthsId,
          date: formValues.date.toISOString().split("T")[0],
          entryType: formValues.entryType || 1,
          comments: formValues.comments || null,
          lines: lines.map((line) => ({
            chartOfAccountId: line.chartOfAccountId,
            debit: Number(line.debit || 0),
            credit: Number(line.credit || 0),
            description: line.description || null,
          })),
        };
        const createRes = await createJournalEntry(createPayload);
        entryIdToConfirm = createRes?.data?.id ?? null;
        if (!entryIdToConfirm) {
          showToast({
            message: language && language?.["Error confirming journal entry"]
              ? language?.["Error confirming journal entry"]
              : "Error confirming journal entry",
            type: "error",
            duration: 20000,
          });
          return;
        }
      }
      await confirmJournalEntry(entryIdToConfirm);
      showToast({
        message: language && language?.["Journal entry confirmed successfully"]
          ? language?.["Journal entry confirmed successfully"]
          : "Journal entry confirmed successfully",
        type: "success",
        duration: 20000,
      });
      setShowConfirmModal(false);
      if (entry) {
        entry.status = "Completed";
        setIsReadOnly(true);
      }
      webNavigate("/" + preLanguage + "/journal-entries");
    } catch (error: any) {
      console.error("Error confirming journal entry:", error);
      showApiError(error, {
        language,
        titleFallback: language && language?.["Error confirming journal entry"]
          ? language?.["Error confirming journal entry"]
          : "Error confirming journal entry",
      });
    } finally {
      setLoading(false);
      webLoadingFalse();
    }
  };

  const handleCancel = async () => {
    if (!id || id === "new") {
      showToast({
        message: language && language?.["Please save the entry first"]
          ? language?.["Please save the entry first"]
          : "Please save the entry first",
        type: "error",
        duration: 20000,
      });
      return;
    }

    webLoadingTrue();
    setLoading(true);
    try {
      await cancelJournalEntry(id);
      showToast({
        message: language && language?.["Journal entry cancelled successfully"]
          ? language?.["Journal entry cancelled successfully"]
          : "Journal entry cancelled successfully",
        type: "success",
        duration: 20000,
      });
      setShowCancelModal(false);
      if (entry) {
        entry.status = "Cancelled";
        setIsReadOnly(true);
      }
      webNavigate("/" + preLanguage + "/journal-entries");
    } catch (error: any) {
      console.error("Error cancelling journal entry:", error);
      showApiError(error, {
        language,
        titleFallback: language && language?.["Error cancelling journal entry"]
          ? language?.["Error cancelling journal entry"]
          : "Error cancelling journal entry",
      });
    } finally {
      setLoading(false);
      webLoadingFalse();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Draft":
        return "#6A7878";
      case "Completed":
        return "#0F7679";
      case "Cancelled":
        return "#BA0E13";
      default:
        return "#6A7878";
    }
  };

  const { totalDebit, totalCredit, difference } = calculateTotals();
  const isBalanced = difference <= 0.01;
  const canConfirm = lines.length > 0 && isBalanced;
  const statusLower = (entry?.status || "").toLowerCase();
  const showConfirmAction = !entry || (statusLower !== "completed" && statusLower !== "cancelled");

  // Create mode: new entry (no id or id is "new", or no entry loaded yet). Always show footer so Save/Confirm are visible.
  const isCreateMode = !id || id === "new" || !entry;
  const showFooter = isCreateMode || !isReadOnly;

  return (
    <>
      <div className="bg-white">
        <div className="flex items-center">
          <div
            className="w-6 h-6 flex items-center justify-center cursor-pointer"
            onClick={() => {
              webNavigate("/" + preLanguage + "/journal-entries");
            }}
          >
            <BackIcon />
          </div>
          <p
            style={{
              fontSize: "14px",
              fontWeight: "500",
              lineHeight: "28px",
              color: "#222525",
            }}
            className="font-inter"
          >
            {language && language?.["Back"] ? language?.["Back"] : "Back"}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        {id === "new" ? (
          <p
            style={{
              margin: "20px 0px",
              fontSize: "20px",
              fontWeight: "500",
              lineHeight: "28px",
              color: "#222525",
            }}
            className="font-inter"
          >
            {language && language?.["New Journal Entry"]
              ? language?.["New Journal Entry"]
              : "New Journal Entry"}
          </p>
        ) : (
          <p
            style={{
              margin: "20px 0px",
              fontSize: "20px",
              fontWeight: "500",
              lineHeight: "28px",
              color: "#222525",
            }}
            className="font-inter"
          >
            {language && language?.["Edit Journal Entry"]
              ? language?.["Edit Journal Entry"]
              : "Edit Journal Entry"}
          </p>
        )}
        <div className="flex items-center" style={{ gap: "16px" }}>
          {entry && (
            <TagStatus value={entry.status} />
          )}
        </div>
      </div>
      <div className="" style={{ height: "calc(100% - 96px - 67px)" }}>
        <form
          className="scroll-container"
          style={{ height: "100%", overflowY: "scroll", paddingBottom: "16px" }}
        >
          {/* Header Info Card */}
          <div
            className=""
            style={{
              padding: "24px",
              border: "1px solid #DBE4E4",
              borderRadius: "12px",
            }}
          >
            <p
              className="text-main font-inter leading-[24px]"
              style={{
                fontSize: "16px",
                fontWeight: "500",
                marginBottom: "24px",
              }}
            >
              {language && language?.["Journal Entry Details"]
                ? language?.["Journal Entry Details"]
                : "Journal Entry Details"}
            </p>
            {entry && (
              <div className="flex items-center gap-2 mb-4">
                <p
                  className="text-[14px] font-inter leading-[20px]"
                  style={{ color: "#4A5353", fontWeight: "500" }}
                >
                  {language && language?.["Entry No."]
                    ? language?.["Entry No."]
                    : "Entry No."}
                  : #{entry.internalNumber}
                </p>
              </div>
            )}
            <div
              className="flex items-start justify-between"
              style={{ gap: "24px", flexWrap: "wrap" }}
            >
              <div
                className="space-y-2"
                style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}
              >
                <div className="flex items-center justify-between">
                  <p
                    className="text-[14px] font-inter leading-[20px]"
                    style={{ color: "#4A5353", fontWeight: "500" }}
                  >
                    {language && language?.["Date"]
                      ? language?.["Date"]
                      : "Date"}
                  </p>
                </div>
                <div className="w-full">
                  <DatePicker
                    selected={formValues.date}
                    onChange={(date: Date | null) => {
                      if (date) setFormValues({ ...formValues, date });
                    }}
                    disabled={isReadOnly}
                    dateFormat="dd-MM-yyyy"
                    className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl outline-none focus-none bg-transparent w-full"
                  />
                </div>
              </div>
              <div
                className="space-y-2"
                style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}
              >
                <div className="flex items-center">
                  <p
                    className="text-[14px] font-inter leading-[20px]"
                    style={{ color: "#4A5353", fontWeight: "500" }}
                  >
                    {language && language?.["Month"]
                      ? language?.["Month"]
                      : "Month"}
                  </p>
                  <p
                    className="text-[14px] font-inter leading-[20px] font-medium"
                    style={{ color: "#BA0E13" }}
                  >
                    {language && language?.["*"] ? language?.["*"] : "*"}
                  </p>
                </div>
                <div className="w-full">
                  <DropdownSearch
                    refKey="month"
                    refs={{ month: monthRef }}
                    openStates={{ month: isMonthDropdownOpen }}
                    open={(key) =>
                      key === "month" && setIsMonthDropdownOpen(true)
                    }
                    close={(key) =>
                      key === "month" && setIsMonthDropdownOpen(false)
                    }
                    data={listMonths}
                    labelExtractor={(item) =>
                      `${item.transactionYear} - ${item.monthSequence}`
                    }
                    value={selectedMonth}
                    setValue={setSelectedMonth}
                    onSelect={handleMonthSelect}
                    disabled={isReadOnly}
                    placeholder={
                      language && language?.["Select month"]
                        ? language?.["Select month"]
                        : "Select month"
                    }
                  />
                </div>
              </div>
              <div className="space-y-2 w-full">
                <div className="flex items-center justify-between">
                  <p
                    className="text-[14px] font-inter leading-[20px]"
                    style={{ color: "#4A5353", fontWeight: "500" }}
                  >
                    {language && language?.["Comments"]
                      ? language?.["Comments"]
                      : "Comments"}
                  </p>
                </div>
                <div className="w-full">
                  <textarea
                    value={formValues.comments}
                    onChange={(e) =>
                      setFormValues({ ...formValues, comments: e.target.value })
                    }
                    disabled={isReadOnly}
                    style={{ height: "88px" }}
                    className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl outline-none focus-none bg-transparent w-full"
                    placeholder={
                      language && language?.["Add comments..."]
                        ? language?.["Add comments..."]
                        : "Add comments..."
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Journal Lines */}
          <div style={{ marginTop: "24px" }}>
            <div className="flex items-center justify-between mb-3">
              <p
                className="text-main font-inter leading-[24px]"
                style={{
                  fontSize: "20px",
                  fontWeight: "500",
                }}
              >
                {language && language?.["Journal Lines"]
                  ? language?.["Journal Lines"]
                  : "Journal Lines"}
              </p>
              {!isReadOnly && (
                <div
                  className="cursor-pointer flex items-center justify-center gap-x-1"
                  style={{
                    backgroundColor: "#EBF0F0",
                    height: "40px",
                    padding: "8px 16px",
                    border: "1px solid transparent",
                    borderRadius: "16px",
                  }}
                  onClick={addLine}
                >
                  <PlusGreenIcon />
                  <p
                    className="text-[14px] leading-[22px] font-inter"
                    style={{ color: "#0F7679", fontWeight: "500" }}
                  >
                    {language && language?.["Add Line"]
                      ? language?.["Add Line"]
                      : "Add Line"}
                  </p>
                </div>
              )}
            </div>
            <div
              style={{
                border: isMobile ? "none" : "1px solid #DBE4E4",
                borderRadius: "16px",
                marginTop: "12px",
              }}
            >
              {!isMobile && (
                <div
                  className="flex items-center"
                  style={{
                    minHeight: "48px",
                    backgroundColor: "#EBF0F0",
                    borderTopRightRadius: "12px",
                    borderTopLeftRadius: "12px",
                  }}
                >
                  <div
                    className="text-xs font-medium font-inter flex items-center"
                    style={{
                      color: "#6A7878",
                      fontSize: "14px",
                      padding: "0px 12px",
                      width: `${calculatedWidth * 40}px`,
                    }}
                  >
                    <p>
                      {language && language?.["No"] ? language?.["No"] : "No"}
                    </p>
                  </div>
                  <div
                    className="text-xs font-medium font-inter flex items-center"
                    style={{
                      color: "#6A7878",
                      padding: "0px 12px",
                      flexGrow: 1,
                    }}
                  >
                    <p>
                      {language && language?.["Chart of Account"]
                        ? language?.["Chart of Account"]
                        : "Chart of Account"}
                    </p>
                  </div>
                  <div
                    className="text-xs font-medium font-inter flex items-center"
                    style={{
                      color: "#6A7878",
                      padding: "0px 12px",
                      width: `${calculatedWidth * 110}px`,
                    }}
                  >
                    <p>
                      {language && language?.["Debit"]
                        ? language?.["Debit"]
                        : "Debit"}
                    </p>
                  </div>
                  <div
                    className="text-xs font-medium font-inter flex items-center"
                    style={{
                      color: "#6A7878",
                      padding: "0px 12px",
                      width: `${calculatedWidth * 110}px`,
                    }}
                  >
                    <p>
                      {language && language?.["Credit"]
                        ? language?.["Credit"]
                        : "Credit"}
                    </p>
                  </div>
                  <div
                    className="text-xs font-medium font-inter flex items-center"
                    style={{
                      color: "#6A7878",
                      padding: "0px 12px",
                      flexGrow: 1,
                    }}
                  >
                    <p>
                      {language && language?.["Comment"]
                        ? language?.["Comment"]
                        : "Comment"}
                    </p>
                  </div>
                  {!isReadOnly && (
                    <div
                      className="text-xs font-medium font-inter flex items-center"
                      style={{
                        color: "#6A7878",
                        padding: "0px 12px",
                        width: `${calculatedWidth * 40}px`,
                      }}
                    ></div>
                  )}
                </div>
              )}
              {lines.map((line, index) => {
                const selectedCoa = asyncCoa.items.find(
                  (coa) => coa.id === line.chartOfAccountId
                );
                const coaDisplayLabel =
                  coaSelectedLabelMap[line.id] ??
                  (selectedCoa ? `${selectedCoa.accountCode} - ${selectedCoa.accountName}` : "");

                if (!dropdownRefs.current[line.id]) {
                  dropdownRefs.current[line.id] = React.createRef<HTMLDivElement>();
                }

                return isMobile ? (
                  <div
                    key={line.id}
                    style={{
                      border: "1px solid #DBE4E4",
                      padding: "16px",
                      borderRadius: "12px",
                      marginBottom: index === lines.length - 1 ? "" : "12px",
                    }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[14px] font-medium" style={{ color: "#222525" }}>
                        {language && language?.["Line"] ? language?.["Line"] : "Line"} {index + 1}
                      </p>
                      {!isReadOnly && (
                        <div
                          className="cursor-pointer"
                          onClick={() => removeLine(line.id)}
                        >
                          <DeleteIcon />
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p
                          className="text-[14px] font-inter leading-[20px] mb-2"
                          style={{ color: "#4A5353", fontWeight: "500" }}
                        >
                          {language && language?.["Chart of Account"]
                            ? language?.["Chart of Account"]
                            : "Chart of Account"}
                        </p>
                        <div className="relative" ref={dropdownRefs.current[line.id]}>
                          <DropdownSearch<ChartOfAccount>
                            refKey={line.id}
                            refs={dropdownRefs.current}
                            openStates={openDropdownIndexes}
                            open={(key) => {
                              setOpenDropdownIndexes({ ...openDropdownIndexes, [key]: true });
                              asyncCoa.loadInitial();
                            }}
                            close={(key) =>
                              setOpenDropdownIndexes({ ...openDropdownIndexes, [key]: false })
                            }
                            data={asyncCoa.items}
                            labelExtractor={(coa) =>
                              `${coa.accountCode} - ${coa.accountName}`
                            }
                            value={
                              openDropdownIndexes[line.id]
                                ? asyncCoa.searchTerm
                                : coaDisplayLabel
                            }
                            setValue={asyncCoa.setSearchTerm}
                            onSelect={(coa) => {
                              updateLine(line.id, "chartOfAccountId", coa.id);
                              setCoaSelectedLabelMap((prev) => ({
                                ...prev,
                                [line.id]: `${coa.accountCode} - ${coa.accountName}`,
                              }));
                              setOpenDropdownIndexes({ ...openDropdownIndexes, [line.id]: false });
                            }}
                            placeholder={
                              language && language?.["Select account"]
                                ? language?.["Select account"]
                                : "Select account"
                            }
                            disabled={isReadOnly}
                            onLoadMore={asyncCoa.loadMore}
                            hasMore={asyncCoa.hasMore}
                            loading={asyncCoa.isLoading}
                            serverSideSearch
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p
                            className="text-[14px] font-inter leading-[20px] mb-2"
                            style={{ color: "#4A5353", fontWeight: "500" }}
                          >
                            {language && language?.["Debit"]
                              ? language?.["Debit"]
                              : "Debit"}
                          </p>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={line.debit || ""}
                            onChange={(e) =>
                              updateLine(line.id, "debit", parseFloat(e.target.value) || 0)
                            }
                            disabled={isReadOnly}
                            className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl outline-none focus-none bg-transparent w-full"
                            style={{ minHeight: "40px" }}
                          />
                        </div>
                        <div>
                          <p
                            className="text-[14px] font-inter leading-[20px] mb-2"
                            style={{ color: "#4A5353", fontWeight: "500" }}
                          >
                            {language && language?.["Credit"]
                              ? language?.["Credit"]
                              : "Credit"}
                          </p>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={line.credit || ""}
                            onChange={(e) =>
                              updateLine(line.id, "credit", parseFloat(e.target.value) || 0)
                            }
                            disabled={isReadOnly}
                            className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl outline-none focus-none bg-transparent w-full"
                            style={{ minHeight: "40px" }}
                          />
                        </div>
                      </div>
                      <div>
                        <p
                          className="text-[14px] font-inter leading-[20px] mb-2"
                          style={{ color: "#4A5353", fontWeight: "500" }}
                        >
                          {language && language?.["Comment"]
                            ? language?.["Comment"]
                            : "Comment"}
                        </p>
                        <input
                          type="text"
                          value={line.description || ""}
                          onChange={(e) =>
                            updateLine(line.id, "description", e.target.value)
                          }
                          disabled={isReadOnly}
                          className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl outline-none focus-none bg-transparent w-full"
                          placeholder={
                            language && language?.["Add comment"]
                              ? language?.["Add comment"]
                              : "Add comment"
                          }
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    key={line.id}
                    className="flex items-center"
                    style={{ minHeight: "48px", borderBottom: "1px solid #E7F1F1" }}
                  >
                    <div
                      className="text-xs font-medium font-inter flex items-center"
                      style={{
                        color: "#6A7878",
                        fontSize: "14px",
                        padding: "0px 12px",
                        width: `${calculatedWidth * 40}px`,
                      }}
                    >
                      <p>{index + 1}</p>
                    </div>
                    <div
                      className="text-xs font-medium font-inter flex items-center"
                      style={{
                        color: "#6A7878",
                        fontSize: "14px",
                        padding: "0px 12px",
                        flexGrow: 1,
                        height: "100%",
                      }}
                    >
                      {!isReadOnly ? (
                        <div
                          className="relative"
                          style={{ width: "100%", height: "100%" }}
                          ref={dropdownRefs.current[line.id]}
                        >
                          <DropdownSearch<ChartOfAccount>
                            refKey={line.id}
                            refs={dropdownRefs.current}
                            openStates={openDropdownIndexes}
                            open={(key) => {
                              setOpenDropdownIndexes({ ...openDropdownIndexes, [key]: true });
                              asyncCoa.loadInitial();
                            }}
                            close={(key) =>
                              setOpenDropdownIndexes({ ...openDropdownIndexes, [key]: false })
                            }
                            data={asyncCoa.items}
                            labelExtractor={(coa) =>
                              `${coa.accountCode} - ${coa.accountName}`
                            }
                            value={
                              openDropdownIndexes[line.id]
                                ? asyncCoa.searchTerm
                                : coaDisplayLabel
                            }
                            setValue={asyncCoa.setSearchTerm}
                            onSelect={(coa) => {
                              updateLine(line.id, "chartOfAccountId", coa.id);
                              setCoaSelectedLabelMap((prev) => ({
                                ...prev,
                                [line.id]: `${coa.accountCode} - ${coa.accountName}`,
                              }));
                              setOpenDropdownIndexes({ ...openDropdownIndexes, [line.id]: false });
                            }}
                            placeholder={
                              language && language?.["Select account"]
                                ? language?.["Select account"]
                                : "Select account"
                            }
                            disabled={isReadOnly}
                            onLoadMore={asyncCoa.loadMore}
                            hasMore={asyncCoa.hasMore}
                            loading={asyncCoa.isLoading}
                            borderNone={true}
                            inItem={true}
                            serverSideSearch
                          />
                        </div>
                      ) : (
                        <p>{coaDisplayLabel}</p>
                      )}
                    </div>
                    <div
                      className="text-xs font-medium font-inter flex items-center"
                      style={{
                        color: "#6A7878",
                        fontSize: "14px",
                        padding: "0px 12px",
                        width: `${calculatedWidth * 110}px`,
                      }}
                    >
                      {!isReadOnly ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={line.debit || ""}
                          onChange={(e) =>
                            updateLine(line.id, "debit", parseFloat(e.target.value) || 0)
                          }
                          disabled={isReadOnly}
                          className="text-[14px] font-inter placeholder:text-main text-main flex items-center justify-center text-right outline-none focus:none bg-transparent"
                          style={{ width: "100%", minHeight: "40px" }}
                        />
                      ) : (
                        <p>{Number(line.debit || 0).toFixed(2)}</p>
                      )}
                    </div>
                    <div
                      className="text-xs font-medium font-inter flex items-center"
                      style={{
                        color: "#6A7878",
                        fontSize: "14px",
                        padding: "0px 12px",
                        width: `${calculatedWidth * 110}px`,
                      }}
                    >
                      {!isReadOnly ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={line.credit || ""}
                          onChange={(e) =>
                            updateLine(line.id, "credit", parseFloat(e.target.value) || 0)
                          }
                          disabled={isReadOnly}
                          className="text-[14px] font-inter placeholder:text-main text-main flex items-center justify-center text-right outline-none focus:none bg-transparent"
                          style={{ width: "100%", minHeight: "40px" }}
                        />
                      ) : (
                        <p>{Number(line.credit || 0).toFixed(2)}</p>
                      )}
                    </div>
                    <div
                      className="text-xs font-medium font-inter flex items-center"
                      style={{
                        color: "#6A7878",
                        fontSize: "14px",
                        padding: "0px 12px",
                        flexGrow: 1,
                      }}
                    >
                      {!isReadOnly ? (
                        <input
                          type="text"
                          value={line.description || ""}
                          onChange={(e) =>
                            updateLine(line.id, "description", e.target.value)
                          }
                          disabled={isReadOnly}
                          className="text-[14px] font-inter placeholder:text-main text-main flex items-center justify-center outline-none focus:none bg-transparent"
                          style={{ width: "100%", height: "22px" }}
                          placeholder={
                            language && language?.["Add comment"]
                              ? language?.["Add comment"]
                              : "Add comment"
                          }
                        />
                      ) : (
                        <p>{line.description || ""}</p>
                      )}
                    </div>
                    {!isReadOnly && (
                      <div
                        className="text-xs font-medium font-inter flex items-center justify-center cursor-pointer"
                        style={{
                          color: "#6A7878",
                          padding: "0px 12px",
                          width: `${calculatedWidth * 40}px`,
                        }}
                        onClick={() => removeLine(line.id)}
                      >
                        <DeleteIcon />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Balance Footer */}
            <div
              style={{
                width: isMobile ? "100%" : "calc(50% - 12px)",
                overflow: "hidden",
                height: "fit-content",
                marginTop: "24px",
              }}
            >
              <div
                style={{
                  padding: "24px",
                  borderTopLeftRadius: "16px",
                  borderTopRightRadius: "16px",
                  border: "1px solid #DBE4E4",
                }}
              >
                <div
                  className="flex items-center justify-between"
                  style={{
                    backgroundColor: "white",
                    height: "36px",
                  }}
                >
                  <div className="flex items-center gap-x-2">
                    <div
                      className="flex items-center justify-center"
                      style={{ width: "24px", height: "24px" }}
                    >
                      <BalanceIcon />
                    </div>
                    <p
                      style={{
                        fontSize: "16px",
                        color: "#222525",
                        fontWeight: "500",
                      }}
                      className="font-inter"
                    >
                      {language && language?.["Total Debit"]
                        ? language?.["Total Debit"]
                        : "Total Debit"}
                    </p>
                  </div>
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#4A5353",
                      fontWeight: "500",
                    }}
                    className="text-main leading-[17px] font-inter"
                  >
                    € {totalDebit.toFixed(2)}
                  </p>
                </div>
                <div
                  className="flex items-center justify-between"
                  style={{
                    backgroundColor: "white",
                    height: "36px",
                  }}
                >
                  <div className="flex items-center gap-x-2">
                    <div
                      className="flex items-center justify-center"
                      style={{ width: "24px", height: "24px" }}
                    >
                      <BalanceIcon />
                    </div>
                    <p
                      style={{
                        fontSize: "16px",
                        color: "#222525",
                        fontWeight: "500",
                      }}
                      className="font-inter"
                    >
                      {language && language?.["Total Credit"]
                        ? language?.["Total Credit"]
                        : "Total Credit"}
                    </p>
                  </div>
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#4A5353",
                      fontWeight: "500",
                    }}
                    className="text-main leading-[17px] font-inter"
                  >
                    € {totalCredit.toFixed(2)}
                  </p>
                </div>
              </div>
              <div
                className="flex items-center justify-between border-b border-[#EBEBED]"
                style={{
                  backgroundColor: isBalanced ? "#EBF0F0" : "#F6E0E0",
                  padding: "16px",
                  border: "1px solid transparent",
                  borderBottomLeftRadius: "16px",
                  borderBottomRightRadius: "16px",
                }}
              >
                <p
                  style={{
                    fontSize: "16px",
                    fontWeight: "500",
                    color: isBalanced ? "#0F7679" : "#BA0E13",
                  }}
                  className="text-main leading-[17px] font-inter"
                >
                  {isBalanced
                    ? language && language?.["BALANCED"]
                      ? language?.["BALANCED"]
                      : "BALANCED"
                    : language && language?.["DIFFERENCE"]
                    ? language?.["DIFFERENCE"]
                    : "DIFFERENCE"}
                </p>
                {!isBalanced && (
                  <p
                    style={{
                      fontSize: "16px",
                      color: "#BA0E13",
                      fontWeight: "500",
                    }}
                    className="text-main leading-[17px] font-inter"
                  >
                    € {difference.toFixed(2)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>

      {showFooter && (
        <div
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 50,
            background: "#FFFFFF",
            borderTop: "1px solid #EBF0F0",
            padding: "12px 16px",
            marginTop: "16px",
          }}
        >
          <div className="flex items-center justify-end" style={{ gap: 12 }}>
            {id && id !== "new" && entry?.status === "Draft" && (
              <button
                type="button"
                onClick={() => setShowCancelModal(true)}
                className="px-4 py-2 rounded-full text-sm font-medium"
                style={{ border: "1px solid #BA0E13", color: "#BA0E13" }}
              >
                {language?.["Cancel"] ?? "Cancel"}
              </button>
            )}

            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-2 rounded-full text-sm font-medium"
              style={{ border: "1px solid #0F7679", color: "#0F7679" }}
            >
              {language?.["Save as Draft"] ?? "Save as Draft"}
            </button>

            {showConfirmAction && (
            <button
              type="button"
              onClick={() => {
                if (!canConfirm) {
                  setShowBalanceError(true);
                  showToast({
                    message: language?.["Debits and credits don't match"] ?? "Debits and credits don't match",
                    type: "error",
                    duration: 5000,
                  });
                  return;
                }
                setShowConfirmModal(true);
              }}
              disabled={!canConfirm}
              className="px-4 py-2 rounded-full text-sm font-medium text-white"
              style={{
                backgroundColor: "#0F7679",
                opacity: canConfirm ? 1 : 0.6,
                cursor: canConfirm ? "pointer" : "not-allowed",
              }}
            >
              {language?.["Confirm"] ?? "Confirm"}
            </button>
            )}
          </div>
        </div>
      )}

      {/* Confirm Modal - centered */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[200]"
          style={{ backgroundColor: "rgba(34, 37, 37, 0.32)" }}
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-lg font-inter"
            style={{ maxWidth: isMobile ? "90%" : "440px", width: "100%", padding: "20px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-[20px] font-semibold leading-[24px]" style={{ color: "#222525" }}>
                {language && language?.["Confirm Journal Entry"]
                  ? language?.["Confirm Journal Entry"]
                  : "Confirm Journal Entry"}
              </p>
              <div
                onClick={() => setShowConfirmModal(false)}
                className="flex items-center justify-center cursor-pointer w-8 h-8 rounded-full hover:bg-[#EBF0F0]"
              >
                <CloseIcon />
              </div>
            </div>
            <p className="text-[14px] mb-6" style={{ color: "#4A5353" }}>
              {language && language?.["Are you sure you want to confirm this journal entry? This will create GL records and cannot be undone."]
                ? language?.["Are you sure you want to confirm this journal entry? This will create GL records and cannot be undone."]
                : "Are you sure you want to confirm this journal entry? This will create GL records and cannot be undone."}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 rounded-full text-[14px] font-medium border border-[#EBF0F0]"
                style={{ color: "#4A5353" }}
              >
                {language && language?.["Cancel"] ? language?.["Cancel"] : "Cancel"}
              </button>
              <button
                type="button"
                className={`px-4 py-2 rounded-full text-[14px] font-medium text-white ${canConfirm ? "" : "opacity-60 cursor-not-allowed"}`}
                style={{ backgroundColor: "#0F7679" }}
                onClick={canConfirm ? handleConfirm : undefined}
              >
                {language && language?.["Confirm"] ? language?.["Confirm"] : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Journal Entry Modal - centered */}
      {showCancelModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[200]"
          style={{ backgroundColor: "rgba(34, 37, 37, 0.32)" }}
          onClick={() => setShowCancelModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-lg font-inter"
            style={{ maxWidth: isMobile ? "90%" : "440px", width: "100%", padding: "20px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-[20px] font-semibold leading-[24px]" style={{ color: "#222525" }}>
                {language && language?.["Cancel Journal Entry"]
                  ? language?.["Cancel Journal Entry"]
                  : "Cancel Journal Entry"}
              </p>
              <div
                onClick={() => setShowCancelModal(false)}
                className="flex items-center justify-center cursor-pointer w-8 h-8 rounded-full hover:bg-[#EBF0F0]"
              >
                <CloseIcon />
              </div>
            </div>
            <p className="text-[14px] mb-6" style={{ color: "#4A5353" }}>
              {language && language?.["Are you sure you want to cancel this journal entry?"]
                ? language?.["Are you sure you want to cancel this journal entry?"]
                : "Are you sure you want to cancel this journal entry?"}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 rounded-full text-[14px] font-medium border border-[#EBF0F0]"
                style={{ color: "#4A5353" }}
              >
                {language && language?.["No"] ? language?.["No"] : "No"}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 rounded-full text-[14px] font-medium text-white"
                style={{ backgroundColor: "#BA0E13" }}
              >
                {language && language?.["Yes, Cancel"] ? language?.["Yes, Cancel"] : "Yes, Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Balance Error / Warning Modal - centered (matches reference: title, message, Go back) */}
      {showBalanceError && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[200]"
          style={{ backgroundColor: "rgba(34, 37, 37, 0.32)" }}
          onClick={() => setShowBalanceError(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-lg font-inter"
            style={{ maxWidth: isMobile ? "90%" : "440px", width: "100%", padding: "20px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-[20px] font-semibold leading-[24px]" style={{ color: "#222525" }}>
                {language && language?.["Debits and credits don't match"]
                  ? language?.["Debits and credits don't match"]
                  : "Debits and credits don't match"}
              </p>
              <div
                onClick={() => setShowBalanceError(false)}
                className="flex items-center justify-center cursor-pointer w-8 h-8 rounded-full hover:bg-[#EBF0F0]"
              >
                <CloseIcon />
              </div>
            </div>
            <p className="text-[14px] mb-4" style={{ color: "#4A5353" }}>
              {language && language?.["To complete this journal entry, total debits must equal total credits."]
                ? language?.["To complete this journal entry, total debits must equal total credits."]
                : "To complete this journal entry, total debits must equal total credits."}
            </p>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-inter" style={{ color: "#4A5353" }}>
                  {language && language?.["Total Debit"] ? language?.["Total Debit"] : "Total Debit"}
                </p>
                <p className="text-sm font-inter font-medium" style={{ color: "#222525" }}>
                  € {totalDebit.toFixed(2)}
                </p>
              </div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-inter" style={{ color: "#4A5353" }}>
                  {language && language?.["Total Credit"] ? language?.["Total Credit"] : "Total Credit"}
                </p>
                <p className="text-sm font-inter font-medium" style={{ color: "#222525" }}>
                  € {totalCredit.toFixed(2)}
                </p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm font-inter font-medium" style={{ color: "#BA0E13" }}>
                  {language && language?.["Difference"] ? language?.["Difference"] : "Difference"}
                </p>
                <p className="text-sm font-inter font-medium" style={{ color: "#BA0E13" }}>
                  € {difference.toFixed(2)}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => setShowBalanceError(false)}
                className="px-4 py-2 rounded-full text-[14px] font-medium border border-[#EBF0F0]"
                style={{ color: "#4A5353", backgroundColor: "#F5F5F5" }}
              >
                {language && language?.["Go back"] ? language?.["Go back"] : "Go back"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

CreateJournalEntry.displayName = "CreateJournalEntry";
export default CreateJournalEntry;
